import React, { useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

const Appointments: React.FC = () => {
  const [date, setDate] = useState<Value>(new Date());
  const [timeSlot, setTimeSlot] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate time slots from 5 PM to 9 PM with 30-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    const startTime = moment().hour(17).minute(0); // 5:00 PM
    const endTime = moment().hour(21).minute(0);   // 9:00 PM

    while (startTime.isBefore(endTime)) {
      slots.push(startTime.format('h:mm A'));
      startTime.add(30, 'minutes');
    }

    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleDateChange = (value: Value) => {
    setDate(value);
    setTimeSlot(null); // Reset time slot when date changes
  };

  const handleTimeSlotClick = (slot: string) => {
    setTimeSlot(slot);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const formResponse = await fetch('https://formspree.io/f/mkgjpwwr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message,
          appointment_date: date instanceof Date ? moment(date).format('MMMM D, YYYY') : '',
          appointment_time: timeSlot,
          _replyto: formData.email,
          _subject: `New Appointment Request from ${formData.name}`,
        }),
      });

      if (!formResponse.ok) {
        throw new Error('Failed to submit form');
      }

      setFormSubmitted(true);
    } catch (error) {
      setError('Failed to book appointment. Please try again or contact us directly at info@sterlingtax.ca or call +1 519-575-2836');
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Disable past dates in calendar
  const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      return date < new Date(new Date().setHours(0, 0, 0, 0));
    }
    return false;
  };

  return (
    <div className="container my-5">
      <div className="row mb-5">
        <div className="col-lg-8 mx-auto text-center">
          <h1 className="display-4 fw-bold">Book an Appointment</h1>
          <p className="lead">
            Schedule a consultation with our tax experts.
          </p>
        </div>
      </div>

      {formSubmitted ? (
        <div className="row">
          <div className="col-md-8 mx-auto">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-5 text-center">
                <div className="mb-4">
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h2 className="mb-3">Appointment Request Received!</h2>
                <p className="mb-4">
                  Thank you for scheduling an appointment with Sterling Tax Solutions. We have received your request
                  for an appointment on <strong>{date instanceof Date ? moment(date).format('MMMM D, YYYY') : ''}</strong> at <strong>{timeSlot}</strong>.
                </p>
                <p className="mb-4">
                  We will confirm your appointment via email shortly. If you have any questions in the meantime,
                  please contact us at <a href="tel:+15195752836">+1 519-575-2836</a>.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setFormSubmitted(false);
                    setDate(new Date());
                    setTimeSlot(null);
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      service: '',
                      message: '',
                    });
                  }}
                >
                  Book Another Appointment
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-lg-4 mb-4 mb-lg-0">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <h3 className="card-title mb-4">Select Date & Time</h3>
                
                <div className="mb-4">
                  <h5 className="mb-3">1. Choose a Date</h5>
                  <div className="calendar-container d-flex justify-content-center mb-3">
                    <Calendar
                      onChange={handleDateChange}
                      value={date}
                      tileDisabled={tileDisabled}
                      minDate={new Date()}
                    />
                  </div>
                  {date && (
                    <p className="mb-0 text-center">
                      Selected Date: <strong>{date instanceof Date ? moment(date).format('MMMM D, YYYY') : ''}</strong>
                    </p>
                  )}
                </div>
                
                <div>
                  <h5 className="mb-3">2. Choose a Time</h5>
                  <div className="time-slots d-flex flex-wrap">
                    {timeSlots.map((slot) => (
                      <div
                        key={slot}
                        className={`appointment-time-slot ${timeSlot === slot ? 'selected' : ''}`}
                        onClick={() => handleTimeSlotClick(slot)}
                      >
                        {slot}
                      </div>
                    ))}
                  </div>
                  {timeSlot && (
                    <p className="mt-3 mb-0 text-center">
                      Selected Time: <strong>{timeSlot}</strong>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <h3 className="card-title mb-4">Your Information</h3>
                
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="name" className="form-label">
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label htmlFor="email" className="form-label">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="phone" className="form-label">
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label htmlFor="service" className="form-label">Service Needed</label>
                      <select
                        className="form-select"
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                      >
                        <option value="">Select a service</option>
                        <option value="Personal Tax Filing">Personal Tax Filing</option>
                        <option value="GST/HST Registration and Filing">GST/HST Registration and Filing</option>
                        <option value="CRA Audit Defence">CRA Audit Defence</option>
                        <option value="Tax Planning">Tax Planning</option>
                        <option value="CPA Designation Mentorship">CPA Designation Mentorship</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="message" className="form-label">Additional Information</label>
                    <textarea
                      className="form-control"
                      id="message"
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                  
                  <div className="d-grid mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={!date || !timeSlot || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Booking...
                        </>
                      ) : (
                        'Book Appointment'
                      )}
                    </button>
                  </div>
                  
                  {(!date || !timeSlot) && (
                    <div className="mt-2 text-center text-danger">
                      Please select both a date and time to book your appointment.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments; 